import { Component, type ErrorInfo, type ReactNode } from 'react';
import ServerErrorPage from '../pages/public/ServerErrorPage';

interface ErrorBoundaryProps {
  readonly children: ReactNode;
}

interface ErrorBoundaryState {
  readonly error: Error | null;
}

/**
 * Top-level React error boundary. Catches render-phase + lifecycle exceptions
 * thrown by descendant components and renders the ServerErrorPage in their
 * place. The ServerErrorPage emits an Application Insights trackException
 * event so the failure shows up in production monitoring.
 *
 * Per `404-500-error-pages-2026-05-16.md`: same component is what the
 * server.js renders for 5xx server-side errors (true HTTP 500).
 *
 * Class component because React's error-boundary contract is class-only.
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // Defensive console output for dev workflows; production AI tracking
    // happens inside ServerErrorPage via trackException.

    console.error('ErrorBoundary caught render error', error, info.componentStack);
  }

  resetError = (): void => {
    this.setState({ error: null });
  };

  render(): ReactNode {
    if (this.state.error) {
      return <ServerErrorPage error={this.state.error} resetError={this.resetError} />;
    }
    return this.props.children;
  }
}
